import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api.js";
import { useAuthStore } from "@/store/auth.js";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";

type AdminUser = {
  id: string;
  email: string;
  email_verified_at: string | null;
  created_at: string;
  deleteTarget?: string; // unused field placeholder
};

export function AdminsPage() {
  const qc = useQueryClient();
  const { user: me } = useAuthStore();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null);
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null);
  const [editEmail, setEditEmail] = useState("");
  const [editError, setEditError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);

  const { data: admins, isLoading } = useQuery({
    queryKey: ["admins"],
    queryFn: () => api.get<AdminUser[]>("/api/super-admin/admins"),
  });

  const createMutation = useMutation({
    mutationFn: (data: { email: string; password: string }) =>
      api.post("/api/super-admin/admins", data),
    onSuccess: () => {
      setCreating(false);
      setForm({ email: "", password: "" });
      qc.invalidateQueries({ queryKey: ["admins"] });
    },
    onError: (e) => setFormError(e instanceof Error ? e.message : "오류"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/super-admin/admins/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admins"] }),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => api.post(`/api/super-admin/admins/${id}/reset-password`),
    onSuccess: (_, id) => {
      const email = admins?.find((a) => a.id === id)?.email ?? "";
      setResetSuccess(`비밀번호 재설정 메일을 ${email}로 발송했습니다.`);
      setTimeout(() => setResetSuccess(null), 4000);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, email }: { id: string; email: string }) =>
      api.put(`/api/super-admin/admins/${id}`, { email }),
    onSuccess: () => {
      setEditTarget(null);
      qc.invalidateQueries({ queryKey: ["admins"] });
    },
    onError: (e) => setEditError(e instanceof Error ? e.message : "오류"),
  });

  function openEdit(admin: AdminUser) {
    setEditTarget(admin);
    setEditEmail(admin.email);
    setEditError(null);
  }

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-500">봉은사</p>
          <h1 className="mt-0.5 text-2xl font-bold text-gray-900">관리자 목록</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-400">{me?.email}</span>
          <button
            onClick={() => {
              api.post("/api/auth/logout").then(() => (window.location.href = "/login"));
            }}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
          >
            로그아웃
          </button>
          <button
            onClick={() => setCreating(true)}
            className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
          >
            + 관리자 추가
          </button>
        </div>
      </div>

      {creating && (
        <div className="mb-6 rounded-xl bg-white p-6 shadow-sm ring-1 ring-black/5">
          <h2 className="mb-4 text-sm font-semibold text-gray-700">새 관리자</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">이메일</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">초기 비밀번호</label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
              />
            </div>
          </div>
          {formError && <p className="mt-2 text-sm text-red-500">{formError}</p>}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={!form.email || !form.password || createMutation.isPending}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              생성 + 인증 메일 발송
            </button>
            <button
              onClick={() => { setCreating(false); setFormError(null); }}
              className="rounded-lg px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {resetSuccess && (
        <div className="mb-4 rounded-xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700 ring-1 ring-green-200">
          ✓ {resetSuccess}
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-400">로딩 중...</p>
      ) : !admins?.length ? (
        <div className="rounded-xl bg-white p-12 text-center shadow-sm ring-1 ring-black/5">
          <p className="text-gray-400">관리자가 없습니다.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/5">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500">
              <tr>
                <th className="px-5 py-3 text-left">이메일</th>
                <th className="px-5 py-3 text-left">이메일 인증</th>
                <th className="px-5 py-3 text-left">생성일</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3.5 font-medium text-gray-900">{admin.email}</td>
                  <td className="px-5 py-3.5">
                    {admin.email_verified_at ? (
                      <span className="text-green-600">✓ 완료</span>
                    ) : (
                      <span className="text-amber-500">미완료</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-gray-400">
                    {new Date(admin.created_at).toLocaleDateString("ko-KR")}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(admin)}
                        className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                      >
                        수정
                      </button>
                      <button
                        onClick={() => resetPasswordMutation.mutate(admin.id)}
                        className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100"
                      >
                        비밀번호 재설정
                      </button>
                      <button
                        onClick={() => setDeleteTarget(admin)}
                        className="rounded px-2 py-1 text-xs text-red-400 hover:bg-red-50"
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="관리자 삭제"
        description={`${deleteTarget?.email} 관리자를 삭제할까요?`}
        confirmLabel="삭제"
        danger
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* Edit modal */}
      {editTarget && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            onClick={() => setEditTarget(null)}
          />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
            <h2 className="mb-4 text-sm font-bold text-gray-900">관리자 수정</h2>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">이메일</label>
              <input
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400"
              />
            </div>
            {editError && <p className="mt-2 text-sm text-red-500">{editError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setEditTarget(null)}
                className="rounded-xl px-4 py-2 text-sm text-gray-500 hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={() =>
                  updateMutation.mutate({ id: editTarget.id, email: editEmail })
                }
                disabled={!editEmail || updateMutation.isPending}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {updateMutation.isPending ? "저장 중..." : "저장"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
